import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { handleAuthorizationCallback, checkAuthBackendHealth, verifyEmailAuthorization } from '../../services/auth-code-service';
import { useAuth } from '../../hooks/useAuth';
import { useStore } from '../../store/store.context';
//import { debugAuthenticationFlow } from '../../utils/auth-debug';

const AuthCallback = () => {
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('Verifying authentication...');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const { store, updateStore } = useStore();
  const hasProcessed = useRef(false);

  const clearAuthSession = () => {
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('userInfo');
    sessionStorage.removeItem('justAuthenticated');
    sessionStorage.removeItem('oauth_state');
    sessionStorage.removeItem('graphToken');
    
    try {
       window.dispatchEvent(new Event('authStateChanged'));
     } catch (error) {
       console.warn('Failed to dispatch auth state change event:', error);
     }
  };

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processCallback = async () => {
      try {
        // Ensure we start with processing status
        setStatus('processing');
        setMessage('Verifying authentication...');
        
        // Run debug report at the start
        //debugAuthenticationFlow();
        
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const error = searchParams.get('error');

        if (error) {
          clearAuthSession();
          setStatus('error');
          setMessage(`Authentication failed: ${error}`);
          return;
        }

        if (!code) {
          clearAuthSession();
          setStatus('error');
          setMessage('No authorization code received');
          return;
        }

        setMessage('Processing authentication credentials...');

        // Skip health check for now and try direct authentication
        console.log('Proceeding directly with token exchange...');
        const result = await handleAuthorizationCallback(code, state, error);


        if (result.success) {
          // Immediately set success status to prevent any error flash
          setStatus('success');
          setMessage(`Welcome ${result.userInfo.name}! Redirecting...`);
          
          // CRITICAL: Clear the appLogout flag in the store FIRST
          updateStore({ appLogout: false });
          
          // Set a flag to indicate we just completed authentication
          sessionStorage.setItem('justAuthenticated', 'true');
          
          // Trigger immediate auth state refresh for the entire app
          window.dispatchEvent(new Event('authStateChanged'));
          
          // Debug session storage after successful auth
          //debugAuthenticationFlow();
          
          // Verify tokens are properly stored
          const storedToken = sessionStorage.getItem('authToken');
          const storedUserInfo = sessionStorage.getItem('userInfo');
          
          if (!storedToken || !storedUserInfo) {
            console.error('Critical error: Tokens not properly stored after successful authentication');
            console.error('Stored token:', !!storedToken);
            console.error('Stored user info:', !!storedUserInfo);
            setStatus('error');
            setMessage('Authentication succeeded but session setup failed. Please try again.');
            return;
          }

          // Verify the authenticated email is in the authorized list
          const email = result.userInfo?.email;
          if (email) {
            setMessage('Verifying access authorization...');
            const { authorized, message: authMessage } = await verifyEmailAuthorization(email);
            if (!authorized) {
              clearAuthSession();
              setStatus('error');
              setMessage(authMessage);
              return;
            }
          }
          
          // If user doesn't have a photo in the result, it will be fetched automatically
          
          // Give the store and state updates time to propagate before navigation
          setTimeout(() => {
            //debugAuthenticationFlow();
            navigate('/', { replace: true });
            
            // Clean up the justAuthenticated flag after navigation
            setTimeout(() => {
              sessionStorage.removeItem('justAuthenticated');
            }, 2000);
          }, 1500); // Give more time for state updates to propagate
        } else {
          // Only set error if we're sure it's a real failure
          console.error('Authentication result indicated failure:', result);
          setStatus('error');
          setMessage(result.message || 'Authentication failed');
        }
      } catch (error) {
        // Only set error status for actual exceptions
        console.error('Callback processing error:', error);
        clearAuthSession();
        
        // Don't immediately set error status - give it a moment in case it's a temporary issue
        setTimeout(() => {
          setStatus('error');
          
          // Provide more specific error messages based on the error
          let errorMessage = error.message || 'Authentication failed';
          
          if (error.message.includes('Unable to connect')) {
            errorMessage = 'Cannot connect to authentication service. Please ensure the authentication backend is running.';
          } else if (error.message.includes('Failed to exchange authorization code')) {
            errorMessage = 'Failed to exchange authorization code. This could be due to: expired code, backend service issues, or configuration problems.';
          } else if (error.message.includes('404')) {
            errorMessage = 'Authentication service endpoint not found. Please check backend configuration.';
          } else if (error.message.includes('500')) {
            errorMessage = 'Authentication service internal error. Please check backend logs.';
          }
          
          console.error('Detailed error info:', {
            originalError: error.message,
            stack: error.stack,
            authBackendUrl: process.env.REACT_APP_AFR_DIGITAL_ASSISTANT_AUTH_URL
          });
          
          setMessage(errorMessage);
        }, 500); // Wait 500ms before showing error to avoid false flashes
      }
    };

    // Set a timeout to prevent hanging in processing state
    const timeout = setTimeout(() => {
      if (status === 'processing') {
        console.warn('Authentication callback timed out');
        setStatus('error');
        setMessage('Authentication processing timed out. Please try again.');
      }
    }, 30000); // 30 second timeout

    processCallback().finally(() => {
      clearTimeout(timeout);
    });

    // Cleanup timeout on component unmount
    return () => clearTimeout(timeout);
  }, [searchParams, navigate, refresh]);

  const getStatusColor = () => {
    switch (status) {
      case 'processing': return '#007bff';
      case 'success': return '#28a745';
      case 'error': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'processing': return '⏳';
      case 'success': return '✅';
      case 'error': return '❌';
      default: return 'ℹ️';
    }
  };

  const getStatusTitle = () => {
    switch (status) {
      case 'processing': return 'Processing Authentication...';
      case 'success': return 'Authentication Successful!';
      case 'error': return 'Authentication Failed';
      default: return 'Processing...';
    }
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      padding: '20px',
      fontFamily: 'Arial, sans-serif',
      backgroundColor: '#f8f9fa'
    }}>
      <div style={{
        backgroundColor: 'white',
        padding: '40px',
        borderRadius: '8px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        textAlign: 'center',
        maxWidth: '500px',
        width: '100%'
      }}>
        <div style={{
          fontSize: '48px',
          marginBottom: '20px'
        }}>
          {getStatusIcon()}
        </div>
        
        <h2 style={{
          color: getStatusColor(),
          marginBottom: '16px',
          fontSize: '24px'
        }}>
          {getStatusTitle()}
        </h2>
        
        <p style={{
          color: '#6c757d',
          fontSize: '16px',
          lineHeight: '1.5',
          marginBottom: '20px'
        }}>
          {message}
        </p>

        {status === 'processing' && (
          <div style={{
            display: 'inline-block',
            width: '20px',
            height: '20px',
            border: '3px solid #f3f3f3',
            borderTop: '3px solid #007bff',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
        )}

        {status === 'error' && (
          <div>
            <button
              onClick={() => navigate('/', { replace: true })}
              style={{
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                marginRight: '10px'
              }}
            >
              Go Home
            </button>
            <button
              onClick={() => window.location.reload()}
              style={{
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Try Again
            </button>
          </div>
        )}
      </div>

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default AuthCallback;
